import React, { ReactElement, ReactNode, useCallback } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import cn from 'classnames';

export interface DropdownItemProps {
  content: ReactNode;
  behavior?: TouchTargetBehavior;
  key: string;
}

interface DropdownProps {
  trigger: ReactElement;
  items: DropdownItemProps[];
  disabled?: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  innerClassName?: string;
}

export const DropdownDivider = () => (
  <div className="border-solid border-b-1 border-gray-150 -mx-3"></div>
);

export const Dropdown = ({
  trigger,
  items,
  disabled = false,
  isOpen,
  setIsOpen,
  innerClassName,
}: DropdownProps) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  const [menuPosition, setMenuPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleDocumentClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node | null;
      // If click is outside both the trigger container and the portal menu, close.
      if (
        target &&
        !containerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    },
    [setIsOpen],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [setIsOpen],
  );

  React.useEffect(() => {
    if (!isOpen) return;

    // Use bubble-phase click so item click handlers run first, then this handler closes.
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleDocumentClick, handleKeyDown]);

  const handleOnItemClick = useCallback(
    (item: DropdownItemProps, e: React.MouseEvent<HTMLAnchorElement>) => {
      if (item.behavior?.type === 'onClick') {
        item.behavior.onClick(e);
      }
      setIsOpen(false);
    },
    [setIsOpen],
  );

  const updateMenuPosition = useCallback(() => {
    if (!containerRef.current || !menuRef.current) return;

    const triggerRect = containerRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();

    const GAP = 8; // mt-2 equivalent

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // --- Vertical positioning (flip if needed) ---
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    const openDownward =
      spaceBelow >= menuRect.height || spaceBelow >= spaceAbove;

    const top = openDownward
      ? triggerRect.bottom + GAP
      : triggerRect.top - menuRect.height - GAP;

    // --- Horizontal positioning (right-aligned, clamped) ---
    const desiredLeft = triggerRect.right - menuRect.width;

    const left = Math.min(
      Math.max(8, desiredLeft), // clamp to left edge
      viewportWidth - menuRect.width - 8, // clamp to right edge
    );

    setMenuPosition({ top, left });
  }, []);

  React.useLayoutEffect(() => {
    if (isOpen) {
      updateMenuPosition();
    }
  }, [isOpen, updateMenuPosition]);

  React.useEffect(() => {
    if (!isOpen) return;

    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  return (
    <div ref={containerRef} tabIndex={-1} className="flex relative">
      <Button
        variant="outline"
        innerClassName={[
          cn('border-0 !ring-0 !ring-offset-0 dropdown-button', innerClassName),
        ]}
        className="!w-full"
        size="small"
        role="button"
        disabled={disabled}
        behavior={{
          type: 'onClick',
          onClick: () => {
            setIsOpen(!isOpen);
          },
        }}
      >
        {trigger}
      </Button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuPosition?.top ?? 0,
              left: menuPosition?.left ?? 0,
              zIndex: 9999,
            }}
            className={classNames('bg-white rounded-lg shadow-12px min-w-44')}
          >
            <ul className="py-2 text-xs text-gray-800 font-medium leading-normal">
              {items.map((item) =>
                item.behavior ? (
                  <TouchTarget
                    key={item.key}
                    content={() => (
                      <li className="block px-4 py-2 hover:bg-gray-100 ">
                        {item.content}
                      </li>
                    )}
                    behavior={
                      item.behavior.type === 'onClick'
                        ? {
                            type: 'onClick',
                            onClick: (e) => handleOnItemClick(item, e),
                          }
                        : item.behavior
                    }
                  />
                ) : (
                  <li key={item.key} className="block px-4 py-2">
                    {item.content}
                  </li>
                ),
              )}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
};
